import {
  Box,
  Flex,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  CloseButton,
  useDisclosure,
  useColorModeValue,
  Image,
  Button,
} from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import { useI18n } from 'lib/i18n/context';
import shortenString from 'lib/shortenString';
import useWeb3AccountWithDomain from 'lib/web3/useAccountWithDomain';
import useWeb3Wallet from 'lib/web3/useWallet';
import IconSvg from 'ui/shared/IconSvg';
import NavigationMobile from 'ui/snippets/navigation/mobile/NavigationMobile';

// 内部钱包按钮子组件，hooks 仅在组件渲染时调用
const DrawerWalletButton = ({ onNavClose }: { onNavClose: () => void }) => {
  const web3Wallet = useWeb3Wallet({ source: 'Header' });
  const web3AccountWithDomain = useWeb3AccountWithDomain(web3Wallet.isConnected);
  const { t } = useI18n();

  const walletDisplayText =
    web3AccountWithDomain.domain ||
    (web3AccountWithDomain.address ? shortenString(web3AccountWithDomain.address) : null);

  const handleClick = React.useCallback(() => {
    if (!web3Wallet.isConnected) {
      onNavClose();
      web3Wallet.openModal();
    }
    // 已连接时只关闭抽屉，用户通过 Header 中的钱包按钮管理
  }, [ web3Wallet, onNavClose ]);

  return (
    <Button
      w="100%"
      h="48px"
      bg="#14DCAC"
      color="#000000"
      borderRadius="8px"
      fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
      fontSize="16px"
      fontWeight={ 500 }
      letterSpacing="0.02em"
      onClick={ handleClick }
      _hover={{ bg: '#10C49A' }}
    >
      { walletDisplayText || t('user.connect') }
    </Button>
  );
};

interface Props {
  isMarketplaceAppPage?: boolean;
}

const Burger = ({ isMarketplaceAppPage }: Props) => {
  const burgerColor = useColorModeValue('gray.600', 'white');
  const drawerBg = useColorModeValue('#F7F7F7', '#171717');
  const headerBg = useColorModeValue('white', '#111111');
  const logoSrc = useColorModeValue(
    '/static/homepage/sidebar-logo-light.svg',
    '/static/homepage/sidebar-logo.svg',
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useI18n();

  return (
    <>
      { /* 汉堡菜单图标按钮 */ }
      <Box padding={ 2 } onClick={ onOpen } cursor="pointer">
        <IconSvg
          name="burger"
          boxSize={ 6 }
          display="block"
          color={ burgerColor }
          aria-label={ t('header.menuButton') }
        />
      </Box>

      <Drawer
        isOpen={ isOpen }
        placement="left"
        onClose={ onClose }
        autoFocus={ false }
      >
        <DrawerOverlay bg="blackAlpha.700"/>
        <DrawerContent maxWidth="100%" width="100%" bg={ drawerBg }>
          <DrawerBody p={ 0 } display="flex" flexDirection="column">

            { /* 顶部区域：Logo + 关闭按钮 + 钱包按钮 */ }
            <Box bg={ headerBg } px={ 4 } pt={ 4 } pb={ 4 } display="flex" flexDirection="column" gap="16px">
              { /* Logo 行 + 关闭按钮 */ }
              <Flex alignItems="center" justifyContent="space-between" height="32px">
                <Image
                  src={ logoSrc }
                  alt="BOT"
                  h="32px"
                  w="auto"
                  flexShrink={ 0 }
                />
                <CloseButton
                  onClick={ onClose }
                  size="md"
                  color={ burgerColor }
                  aria-label="Close menu"
                />
              </Flex>

              { /* 全宽钱包按钮 */ }
              { config.features.blockchainInteraction.isEnabled && (
                <DrawerWalletButton onNavClose={ onClose }/>
              ) }
            </Box>

            { /* 导航菜单列表 */ }
            <Box flex={ 1 } overflowY="auto">
              <NavigationMobile
                onNavLinkClick={ onClose }
                isMarketplaceAppPage={ isMarketplaceAppPage }
              />
            </Box>

          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Burger;
